import React, { useEffect, useState } from 'react';
import Select from '../../../components/Select';
import LoadingSpinner from '../../../components/LoadingSpinner';
import RightArrow from './ic-arrow-next.svg';
import Link from 'next/link';
import s from './OpenPosition.module.scss';

interface Position {
  id: string;
  title: string;
  metadata: Array<{ value: string }>;
  offices: Array<{ name: string }>;
  departments: Array<{ name: string }>;
}

interface SelectOption {
  name: string;
  value: string;
}

interface OpenPositionProps {
  openPositions: Position[];
  isPositionsLoading: boolean;
}

interface DepartmentPositions {
  [key: string]: Position[];
}

const OpenPosition: React.FC<OpenPositionProps> = ({ openPositions, isPositionsLoading }) => {
  const [departmentWisePositions, setDepartmentWisePositions] = useState<DepartmentPositions>({});
  const [selectedOffice, setSelectedOffice] = useState<string>('All Offices');
  const [officesList, setOfficesList] = useState<SelectOption[]>([
    { name: 'All Offices', value: 'All Offices' }
  ]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All Department');
  const [departmentsList, setDepartmentsList] = useState<SelectOption[]>([
    { name: 'All Department', value: 'All Department' }
  ]);

  const onDepartmentSelected = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDepartment(e.target.value);
  };

  const onOfficeSelected = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOffice(e.target.value);
  };

  // group positions by department
  useEffect(() => {
    const tempDepartmentWisePositions: DepartmentPositions = {};
    const tempOfficesSet = new Set<string>();

    openPositions.forEach((position) => {
      tempOfficesSet.add(position.offices[0].name);
      position.departments.forEach((department) => {
        if (tempDepartmentWisePositions[department.name]) {
          tempDepartmentWisePositions[department.name].push(position);
        } else {
          tempDepartmentWisePositions[department.name] = [position];
        }
      });
    });
    
    setDepartmentWisePositions({ ...tempDepartmentWisePositions });

    setDepartmentsList([
      { name: 'All Department', value: 'All Department' },
      ...Object.keys(tempDepartmentWisePositions).map((department) => ({
        name: department,
        value: department,
      })),
    ]);

    setOfficesList([
      { name: 'All Offices', value: 'All Offices' },
      ...Array.from(tempOfficesSet).map((value) => ({ 
        name: value, 
        value 
      })),
    ]);
  }, [openPositions]);

  // filter positions by department or office
  useEffect(() => {
    const tempDepartmentWisePositions: DepartmentPositions = {};

    openPositions.forEach((position) => {
      if (selectedOffice === 'All Offices' || position.offices[0].name === selectedOffice) {
        position.departments.forEach((department) => {
          if (selectedDepartment !== 'All Department' && department.name !== selectedDepartment) {
            return;
          }
          if (tempDepartmentWisePositions[department.name]) {
            tempDepartmentWisePositions[department.name].push(position);
          } else {
            tempDepartmentWisePositions[department.name] = [position];
          }
        });
      }
    });
    
    setDepartmentWisePositions({ ...tempDepartmentWisePositions });
  }, [selectedDepartment, selectedOffice, openPositions]);

  return (
    <div id="openPositions" className={s.container} data-testid="openPositionsContainer">
      <h2 data-aos="fade-up" className={s.title}>
        Open Positions
      </h2>
      <div data-aos="fade-up" className={s.filters}>
        <Select
          dataTestId="departmentFilters"
          value={selectedDepartment}
          label="Department"
          items={departmentsList}
          onChange={onDepartmentSelected}
        />
        <Select
          dataTestId="officesFilters"
          value={selectedOffice}
          label="Offices"
          items={officesList}
          onChange={onOfficeSelected}
        />
      </div>
      <div className={s.departmentContainer}>
        {isPositionsLoading && (
          <div className={s.loadingSpinner} data-testid="loadingSpinnerContainer">
            <LoadingSpinner />
          </div>
        )}

        {!isPositionsLoading &&
          Object.keys(departmentWisePositions).map((department) => (
            <div className={s.departmentContent} key={department} data-testid="departments">
              <h5 data-aos="fade-up" className={s.departmentTitle} data-testid="departmentTitle">
                {department}
              </h5>
              {departmentWisePositions[department].map((position) => (
                <Link key={position.id} href={`/careers/${position.id}`} data-testid="positions">
                  <div data-aos="fade-up" className={s.positionContainer}>
                    <div className={s.positionContent}>
                      <h3 data-testid="positionTitle">{position.title}</h3>
                      <div className={s.positionTail}>
                        <p>
                          {position?.metadata[0]?.value && position.offices[0]?.name
                            ? `${position.metadata[0].value}  •  ${position.offices[0].name}`
                            : position?.metadata[0]?.value || position.offices[0]?.name || ''}
                        </p>
                      </div>
                    </div>
                    <div className={s.arrow}>
                      <RightArrow />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ))}

        {!isPositionsLoading && Object.keys(departmentWisePositions).length === 0 && (
          <h3 data-aos="fade-up" className={s.NoJobsMessage} data-testid="NoJobsMessage">
            No Jobs Found
          </h3>
        )}
      </div>
    </div>
  );
};

export default OpenPosition;
